import React, { useState, useEffect } from 'react'
import { useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { sendNotificationUser } from '../apollo'
import { validateFunc } from '../constraints/constraints'
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress
} from '@mui/material'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import SendIcon from '@mui/icons-material/Send'

const NOTIFICATION_USER = gql`${sendNotificationUser}`

const Notifications = props => {
  const { t } = props
  const [notificationTitle, setNotificationTitle] = useState('')
  const [notificationBody, setNotificationBody] = useState('')
  const [titleError, setTitleError] = useState(null)
  const [bodyError, setBodyError] = useState(null)
  const [mainError, setMainError] = useState('')
  const [success, setSuccess] = useState('')

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000)
      return () => clearTimeout(timer)
    }
  }, [success])

  const [mutate, { loading }] = useMutation(NOTIFICATION_USER, {
    onCompleted: () => {
      setSuccess(t('NotificationSentAuccessfully'))
      setNotificationTitle('')
      setNotificationBody('')
      setTitleError(null)
      setBodyError(null)
    },
    onError: () => setMainError(t('ActionFailedTryAgain'))
  })

  const onSubmitValidation = () => {
    const nTitleError = !validateFunc({ notificationTitle }, 'notificationTitle')
    const nBodyError = !validateFunc({ notificationBody }, 'notificationBody')
    setTitleError(nTitleError)
    setBodyError(nBodyError)
    return nTitleError && nBodyError
  }

  return (
    <>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #1e40af 100%)', px: { xs: 2, md: 4 }, pt: 3.5, pb: 5, position: 'relative', overflow: 'hidden', '&::before': { content: '""', position: 'absolute', top: -60, right: -60, width: 260, height: 260, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1 }}>
          <Box sx={{ width: 52, height: 52, borderRadius: '14px', background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <NotificationsActiveIcon sx={{ color: '#93c5fd', fontSize: 26 }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', color: '#fff', lineHeight: 1.15, letterSpacing: '-0.02em' }}>Notifications</Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.55)', mt: 0.3 }}>Broadcast push notifications to all customers</Typography>
          </Box>
        </Box>
      </Box>

      <Container maxWidth="xl" sx={{ mt: 3, pb: 4 }}>
        <Paper elevation={0} sx={{ borderRadius: '20px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,0.06)', maxWidth: 600 }}>
          <Box sx={{ px: 3, py: 2.5, borderBottom: '1px solid #f1f5f9', background: '#fafbfc', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 38, height: 38, borderRadius: '10px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <NotificationsActiveIcon sx={{ fontSize: 20, color: '#3b82f6' }} />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1e293b' }}>Broadcast Notification</Typography>
              <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', mt: 0.2 }}>Send a push notification to all users</Typography>
            </Box>
          </Box>

          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Notification Title</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter notification title..."
                  value={notificationTitle}
                  onChange={e => { setNotificationTitle(e.target.value); setTitleError(null) }}
                  InputProps={{ sx: { borderRadius: '10px', fontSize: '0.9rem', bgcolor: '#fff' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: titleError === false ? '#fca5a5' : titleError === true ? '#86efac' : '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#93c5fd' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                    }
                  }}
                />
                {titleError === false && <Typography sx={{ fontSize: '0.72rem', color: '#ef4444', mt: 0.5, ml: 0.5 }}>Title is required</Typography>}
              </Box>

              <Box>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#374151', mb: 0.75 }}>Notification Body</Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Enter notification message..."
                  value={notificationBody}
                  onChange={e => { setNotificationBody(e.target.value); setBodyError(null) }}
                  multiline
                  rows={4}
                  InputProps={{ sx: { borderRadius: '10px', fontSize: '0.9rem', bgcolor: '#fff' } }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: bodyError === false ? '#fca5a5' : bodyError === true ? '#86efac' : '#e2e8f0' },
                      '&:hover fieldset': { borderColor: '#93c5fd' },
                      '&.Mui-focused fieldset': { borderColor: '#3b82f6' }
                    }
                  }}
                />
                {bodyError === false && <Typography sx={{ fontSize: '0.72rem', color: '#ef4444', mt: 0.5, ml: 0.5 }}>Message body is required</Typography>}
              </Box>

              <Button
                variant="contained"
                startIcon={loading ? <CircularProgress size={15} color="inherit" /> : <SendIcon />}
                disabled={loading}
                onClick={e => {
                  e.preventDefault()
                  setMainError('')
                  setSuccess('')
                  if (onSubmitValidation()) {
                    mutate({ variables: { notificationTitle, notificationBody } })
                  }
                }}
                sx={{ background: 'linear-gradient(135deg,#3b82f6,#2563eb)', color: '#fff', fontWeight: 700, px: 3, py: 1.1, borderRadius: '10px', boxShadow: '0 4px 14px rgba(59,130,246,0.4)', textTransform: 'none', alignSelf: 'flex-start', '&:hover': { background: 'linear-gradient(135deg,#2563eb,#1d4ed8)', transform: 'translateY(-1px)' }, transition: 'all 0.2s ease' }}>
                {loading ? 'Sending...' : 'Send Notification'}
              </Button>
            </Box>

            <Box mt={2}>
              {success && <Alert severity="success" variant="filled" sx={{ borderRadius: '10px', fontSize: '0.83rem' }}>{success}</Alert>}
              {mainError && <Alert severity="error" variant="filled" sx={{ borderRadius: '10px', fontSize: '0.83rem' }}>{mainError}</Alert>}
            </Box>
          </Box>
        </Paper>
      </Container>
    </>
  )
}

export default withTranslation()(Notifications)